import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { useAccessibilityMessage } from "shared/types/useAccessibilityMessage"
import { RowRendererProps } from "shared/ui/table/types"

import { ProductPositionAddonRowCellType } from "widgets/product-position-table/hooks/useProductPositionAddonRowCells"

import { ProductPositionTableRowWrapper } from "../../ProductPositionTableRowWrapper"
import { ProductPositionRowClarificationRequestCell } from "../cells/ProductPositionRowClarificationRequestCell"
import { ProductPositionRowPositionCell } from "../cells/ProductPositionRowPositionCell"
import { ProductPositionRowTitleCell } from "../cells/ProductPositionRowTitleCell"
import { ProductPositionRowVariantKeyCell } from "../cells/ProductPositionRowVariantKeyCell"
import { useProductPositionTableProductPositionAddonRowViewModel } from "../hooks/useProductPositionTableProductPositionAddonRowViewModel"

interface ProductPositionTableProductPositionAddonRowViewProps
  extends RowRendererProps<ProductPositionAddon> {
  title?: string | null
}

export const ProductPositionTableProductPositionAddonRowView = React.memo(
  (props: ProductPositionTableProductPositionAddonRowViewProps) => {
    const { t } = useTranslation()
    const {
      item,
      index,
      title,
      isSelected,
      isDragDisabled,
      draggedElementID,
      isFocused,
      rowElementProps,
      style,
      position,
      getCellProps,
      showClarificationRequestColumn,
      showSelectionCheckbox
    } = useProductPositionTableProductPositionAddonRowViewModel(props)
    const { variantKey } = item

    useAccessibilityMessage(
      {
        id: "focused-row-room-info",
        text: t("ProjectPage.Accessibility.ProductRowFocus", {
          title: variantKey,
          position,
          configuration: variantKey
        })
      },
      isFocused
    )

    return (
      <ProductPositionTableRowWrapper
        {...rowElementProps}
        index={index}
        style={style}
        isDragDisabled={isDragDisabled || !!draggedElementID}
        data-key={variantKey}
        data-rowtype={item.__typename}
        className={classNames(
          rowElementProps?.className,
          "product-position-addon",
          isSelected ? "highlight" : undefined,
          draggedElementID ? "disable-hover" : undefined
        )}
      >
        <div role="gridcell" aria-hidden className="table-row-cell left-space">
          <div className="project-table-row__select-indicator" />
        </div>
        {showSelectionCheckbox ? (
          <div role="gridcell" aria-hidden className="table-row-cell" />
        ) : null}
        <div
          role="gridcell"
          aria-hidden
          className="table-row-cell collapse-button"
        />
        <ProductPositionRowPositionCell
          {...getCellProps?.(ProductPositionAddonRowCellType.POSITION)}
          position={position}
        />
        {showClarificationRequestColumn ? (
          <ProductPositionRowClarificationRequestCell
            {...getCellProps?.(
              ProductPositionAddonRowCellType.CLARIFICATION_REQUEST
            )}
            hasClarificationRequest={null}
          />
        ) : null}
        <ProductPositionRowTitleCell
          {...getCellProps?.(ProductPositionAddonRowCellType.NAME)}
          title={title}
        />
        <ProductPositionRowVariantKeyCell
          {...getCellProps?.(ProductPositionAddonRowCellType.VARIANT_KEY)}
          variantKey={item.variantKey}
        />
        <div
          role="gridcell"
          aria-hidden
          className="table-row-cell right-space"
        />
      </ProductPositionTableRowWrapper>
    )
  }
)
